import { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { graphql } from '@/gql';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { FormField, FormLayout, FormSection } from '@/components/ui/FormLayout';
import { Input } from '@/components/ui/Input';
import { QueryErrorNotice } from '@/components/ui/QueryErrorNotice';
import { graphqlErrorMessage } from '@/lib/graphqlError';

const ScopeTicketSettingsDoc = graphql(/* GraphQL */ `
  query ScopeTicketSettings {
    scopeTicketSettings {
      scopeId
      taxPercentage
      feeTaxPercentage
      effectiveFeeTaxPercentage
      ticketPointsPerEuro
      configured
      exactTicketMappingConfigured
      updatedAt
    }
  }
`);

const SaveScopeTicketSettingsDoc = graphql(/* GraphQL */ `
  mutation SaveScopeTicketSettings(
    $taxPercentage: Float!
    $feeTaxPercentage: Float
    $ticketPointsPerEuro: Int!
  ) {
    saveScopeTicketSettings(
      taxPercentage: $taxPercentage
      feeTaxPercentage: $feeTaxPercentage
      ticketPointsPerEuro: $ticketPointsPerEuro
    ) {
      taxPercentage
      feeTaxPercentage
      effectiveFeeTaxPercentage
      ticketPointsPerEuro
      configured
      exactTicketMappingConfigured
    }
  }
`);

export function TicketsTab() {
  const { data, previousData, loading, error, refetch } = useQuery(
    ScopeTicketSettingsDoc,
    { errorPolicy: 'all' },
  );
  const settings = (data ?? previousData)?.scopeTicketSettings;

  const [tax, setTax] = useState('');
  const [feeTax, setFeeTax] = useState('');
  const [pointsPerEuro, setPointsPerEuro] = useState('35');
  const [seeded, setSeeded] = useState(false);
  if (settings && !seeded) {
    setSeeded(true);
    setTax(settings.taxPercentage != null ? String(settings.taxPercentage) : '');
    setFeeTax(settings.feeTaxPercentage != null ? String(settings.feeTaxPercentage) : '');
    setPointsPerEuro(
      settings.ticketPointsPerEuro != null ? String(settings.ticketPointsPerEuro) : '35',
    );
  }

  const [save, { loading: saving }] = useMutation(SaveScopeTicketSettingsDoc);
  const [saveError, setSaveError] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaveError(null);
    const taxPercentage = Number.parseFloat(tax);
    if (!Number.isFinite(taxPercentage)) {
      setSaveError('Set the admission tax percentage. Ticket sales are rejected until this is configured.');
      return;
    }
    const feeTaxPercentage = feeTax.trim() === '' ? null : Number.parseFloat(feeTax);
    if (feeTax.trim() !== '' && !Number.isFinite(feeTaxPercentage)) {
      setSaveError('Booking-fee tax must be a number, or left empty to use the admission rate.');
      return;
    }
    const ticketPointsPerEuro = Number.parseInt(pointsPerEuro, 10);
    if (!Number.isFinite(ticketPointsPerEuro) || ticketPointsPerEuro < 1) {
      setSaveError('Points per euro must be a whole number of at least 1.');
      return;
    }
    try {
      await save({
        variables: { taxPercentage, feeTaxPercentage, ticketPointsPerEuro },
      });
      await refetch();
    } catch (err) {
      setSaveError(graphqlErrorMessage(err));
    }
  };

  if (loading && !settings && !error) {
    return <p className="text-sm text-gray-500">Loading ticket tax settings…</p>;
  }

  return (
    <FormLayout
      title="Ticket tax"
      description="One rate per scope, applied to admission and the booking fee. Stages carry prices only — never a tax field. Ticket sales in this scope are rejected until this is set, and until the Exact ticket GL is mapped on the Exact Online tab. ExpoApp can pay one ticket’s admission with points at the rate below; extra tickets and the booking fee are still charged."
      onSubmit={handleSubmit}
      footer={
        <div className="flex flex-wrap items-center gap-3">
          <Button type="submit" id="admin.config.ticketTaxSave" loading={saving}>
            Save ticket tax
          </Button>
          {saveError ? <span className="text-sm text-red-600">{saveError}</span> : null}
        </div>
      }
    >
      <FormSection title="This scope">
        {error ? (
          <QueryErrorNotice error={error} title="Could not load ticket tax settings" />
        ) : null}
        <div className="flex flex-wrap gap-2">
          <Badge tone={settings?.configured ? 'success' : 'warning'}>
            {settings?.configured ? 'Tax configured' : 'Tax not set — sales rejected'}
          </Badge>
          <Badge tone={settings?.exactTicketMappingConfigured ? 'success' : 'warning'}>
            {settings?.exactTicketMappingConfigured
              ? 'Exact ticket GL mapped'
              : 'Exact ticket GL missing'}
          </Badge>
        </div>
        <FormField
          label="Admission tax %"
          required
          description="Applied to ticket admission. Fail closed while empty."
        >
          <Input
            id="admin.config.ticketTax"
            type="number"
            step="0.01"
            min="0"
            value={tax}
            onChange={(e) => setTax(e.target.value)}
            placeholder="e.g. 9 or 21"
          />
        </FormField>
        <FormField
          label="Booking-fee tax % (optional)"
          description="Leave empty to tax the booking fee at the admission rate. Use this only if NL rates diverge."
        >
          <Input
            type="number"
            step="0.01"
            min="0"
            value={feeTax}
            onChange={(e) => setFeeTax(e.target.value)}
            placeholder="Same as admission"
          />
        </FormField>
        {settings?.effectiveFeeTaxPercentage != null ? (
          <p className="text-xs text-gray-500">
            Effective fee rate right now: {settings.effectiveFeeTaxPercentage}%
          </p>
        ) : null}
        <FormField
          label="Points per euro (ticket admission)"
          required
          description="ExpoApp only. Guests who have enough points can pay one ticket’s admission at this rate (default 35). Extra tickets and the booking fee are still paid in euros. A later change does not rewrite an in-flight hold."
        >
          <Input
            id="admin.config.ticketPointsPerEuro"
            type="number"
            step="1"
            min="1"
            value={pointsPerEuro}
            onChange={(e) => setPointsPerEuro(e.target.value)}
            placeholder="35"
          />
        </FormField>
      </FormSection>
    </FormLayout>
  );
}
